// stagingEnv.ts — CORE-1507: the production-like stand (CORE-634) as a third
// target of the native env switcher.
//
// Kept apart from `envSwitch.HOSTS` on purpose. `hostForRole` walks that table
// to decide what counts as «another env's host», and the stand's own web bundle
// — baked with exactly these hosts and served from a non-prod surface — would
// have them rewritten to dev.
//
// Bare hostnames, like `envSwitch.HOSTS`: the deploy scrub rewrites full
// `https://` URLs of a foreign env in a web bundle (CORE-704).

/** The stand's hosts per role. Keep in sync with .env.staging. */
export const STAGING_HOSTS = {
  api: 'api.ag1.tmless.org',
  chatApi: 'chat-api.ag1.tmless.org',
  chatHub: 'agape-chat.ag1.tmless.org',
} as const;

/**
 * The same roles as reached through the Moscow edge
 * (docs/infra/ru.ag1.tmless.org.nginx.conf). One host, path prefixes stripped
 * by the edge — the layout of the dev edge in ruDevEdge.ts.
 */
export const STAGING_RU_EDGE_HOSTS = {
  api: 'api.ru.ag1.tmless.org',
  chatApi: 'api.ru.ag1.tmless.org/chat-api',
  chatHub: 'api.ru.ag1.tmless.org/agape-chat',
} as const;

export const STAGING_RU_EDGE_ORIGIN = 'https://api.ru.ag1.tmless.org';

/**
 * CORE-1589 — the stand's frontend as served from the Moscow edge. A bundle
 * loaded from there talks to the stand through the edge as well: from Russia
 * the direct leg to *.ag1.tmless.org dies around 16 KB, which is the very
 * reason the page was opened on this host. Same rule as `isRuDevEdgeWebHost`.
 */
export function isRuStagingEdgeWebHost(host: string): boolean {
  return host === 'app.ru.ag1.tmless.org';
}

type StagingRole = keyof typeof STAGING_HOSTS;

/**
 * Rewrite a URL's api/chat host to the stand. `bakedHosts` are the host tables
 * of the envs a build can be compiled against; everything else (S3, api-ru,
 * third-party) passes through. Anchored to `://<host>` so a query string that
 * echoes a host is left alone.
 */
export function toStagingHost(
  url: string,
  bakedHosts: Array<Record<string, string>>,
  viaRuEdge: boolean,
): string {
  for (const role of Object.keys(STAGING_HOSTS) as StagingRole[]) {
    const to = (viaRuEdge ? STAGING_RU_EDGE_HOSTS : STAGING_HOSTS)[role];
    const froms = bakedHosts.map((hosts) => hosts[role]).filter(Boolean);
    // A stray direct stand URL must not skip the edge either.
    if (viaRuEdge) froms.push(STAGING_HOSTS[role]);
    for (const from of froms) {
      if (url.includes(`://${from}`)) return url.replace(`://${from}`, `://${to}`);
    }
  }
  return url;
}
